// Adding people from Google Contacts — the way round phones whose built-in
// contact picker won't let you choose (Samsung + Chrome). A short-lived Google
// token for "see your contacts", then the People API straight from this device:
// only names and photos are read, nothing is stored but the people you pick.

import { devicePrefs } from './device.ts';
import { googleOAuth } from './google.ts';
import { avatarFrom } from './visitors.ts';

export const CONTACTS_SCOPE = 'https://www.googleapis.com/auth/contacts.readonly';

export interface GoogleContact {
  id: string;
  name: string;
  photo?: string;
}

let cached: { token: string; until: number } | undefined;

/** Google's "allow Arlo HQ to see your contacts" popup. Call straight from a tap. */
export function contactsToken(clientId: string): Promise<string> {
  if (cached && cached.until - Date.now() > 5 * 60_000) return Promise.resolve(cached.token);
  const oauth2 = window.google?.accounts?.oauth2;
  if (!oauth2) return googleOAuth().then(() => Promise.reject(new Error('Google was still loading — tap again')));
  return new Promise((resolve, reject) => {
    oauth2
      .initTokenClient({
        client_id: clientId,
        scope: CONTACTS_SCOPE,
        login_hint: devicePrefs().account?.email,
        callback: (r: { access_token?: string; expires_in?: number; error?: string; error_description?: string; scope?: string }) => {
          if (!r.access_token) return reject(new Error(r.error === 'access_denied' ? 'Google Contacts wasn’t allowed' : (r.error_description ?? 'Google didn’t sign in')));
          if (r.scope && !r.scope.includes(CONTACTS_SCOPE)) return reject(new Error('Tick “see your contacts” on Google’s screen to add people'));
          cached = { token: r.access_token, until: Date.now() + Math.max(60, (r.expires_in ?? 3600) - 60) * 1000 };
          resolve(r.access_token);
        },
        error_callback: (e: { type?: string }) => reject(new Error(e.type === 'popup_closed' ? 'Closed before finishing' : 'Couldn’t open Google’s sign-in — allow pop-ups for this site')),
      })
      .requestAccessToken();
  });
}

interface PeopleResponse {
  connections?: { resourceName: string; names?: { displayName?: string }[]; photos?: { url?: string; default?: boolean }[] }[];
  nextPageToken?: string;
  error?: { message?: string; status?: string };
}

/** Everyone in Google Contacts with a name (and their photo, if they have a real one). */
export async function listGoogleContacts(token: string): Promise<GoogleContact[]> {
  const out: GoogleContact[] = [];
  let page: string | undefined;
  for (let i = 0; i < 10; i++) {
    const u = new URL('https://people.googleapis.com/v1/people/me/connections');
    u.searchParams.set('personFields', 'names,photos');
    u.searchParams.set('pageSize', '1000');
    u.searchParams.set('sortOrder', 'FIRST_NAME_ASCENDING');
    if (page) u.searchParams.set('pageToken', page);
    let res: Response;
    try {
      res = await fetch(u, { headers: { Authorization: `Bearer ${token}` } });
    } catch {
      throw new Error('Couldn’t reach Google — are you online?');
    }
    const j = (await res.json().catch(() => ({}))) as PeopleResponse;
    if (!res.ok) {
      if (res.status === 401) cached = undefined;
      const msg = j.error?.message ?? `Google said ${res.status}`;
      // The usual first-time snag: the People API isn't switched on for this Google project.
      throw new Error(/has not been used|is disabled|SERVICE_DISABLED/i.test(msg) ? 'Google Contacts isn’t switched on for Arlo HQ yet: in Google Cloud, enable the “People API” for the project that has Arlo’s sign-in, then try again.' : msg);
    }
    for (const c of j.connections ?? []) {
      const name = c.names?.find((n) => n.displayName?.trim())?.displayName?.trim();
      if (!name) continue;
      const photo = c.photos?.find((p) => p.url && !p.default)?.url;
      out.push({ id: c.resourceName, name, photo });
    }
    page = j.nextPageToken;
    if (!page) break;
  }
  return out;
}

/** A Google contact photo as a small data URL (or its link, if the browser can't copy it). */
export async function googlePhoto(url: string): Promise<string | undefined> {
  const sized = url.replace(/=s\d+(-[a-z]+)?$/, '') + '=s160';
  try {
    const res = await fetch(sized);
    if (res.ok) return (await avatarFrom(await res.blob())) ?? sized;
  } catch {
    /* no CORS: fall back to the link */
  }
  return sized;
}
